import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, Brain, Link2, Bot } from 'lucide-react';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const servicesDropdownRef = useRef(null);
  const location = useLocation();

  // Close dropdown when clicking outside
useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileServicesOpen(false);
  }, [location]);

  const services = [
    {
      name: 'Integration',
      path: '/services/integration',
      icon: <Link2 className="w-5 h-5" />,
      description: 'End-to-end system integrations'
    },
    {
      name: 'Automation',
      path: '/services/automation',
      icon: <Bot className="w-5 h-5" />,
      description: 'AI powered process automation'
    },
    {
      name: 'Fractional and Advisory',
      path: '/services/fractional',
      icon: <Brain className="w-5 h-5" />,
      description: 'Fractional and advisory'
    }
  ];

  return (
    <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-200 shadow-sm">
      <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-8 h-8 bg-white/80 rounded-sm flex items-center justify-center">
              <img 
                src="/t.png" 
                alt="Tare Labs" 
                className="h-full flex items-center justify-center"
              />
            </div>
          </div>
          <div className="text-2xl font-bold text-black">
            Tare Labs
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">

          <div className="relative" ref={servicesDropdownRef}>
            <button
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              className="flex items-center space-x-1 text-gray-700 hover:text-black transition-colors font-medium"
            >
              <span>Services</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Dropdown Menu */}
            {isServicesOpen && (
              <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                {services.map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    className="flex items-start px-4 py-3 hover:bg-gray-50 transition-colors group"
                    onClick={() => setIsServicesOpen(false)}
                  >
                    <div className="w-10 h-10 bg-black text-white flex items-center justify-center rounded mr-3 group-hover:scale-110 transition-transform">
                      {service.icon}
                    </div>
                    <div>
                      <div className="font-semibold text-black group-hover:text-black">
                        {service.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {service.description}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/tools/freight-carbon-calculator"
            className="text-gray-700 hover:text-black transition-colors font-medium"
          >
            Carbon Calculator
          </Link>
          <Link to="/blog" className="text-gray-700 hover:text-black transition-colors font-medium">
            Blog
          </Link>
          <Link to="/about" className="text-gray-700 hover:text-black transition-colors font-medium">
            About
          </Link>
          <Link to="/#contact" className="text-gray-700 hover:text-black transition-colors font-medium">
            Contact
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <Menu className="w-6 h-6" />
        </button>

        {/* Mobile Menu */}

        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-20 right-6 bg-white shadow-2xl rounded-2xl p-6 z-50 min-w-[280px]">
            <div className="space-y-2">

              {/* Services Accordion */}
              <div>
                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className="flex items-center justify-between w-full py-2 font-medium"
                >
                  <span>Services</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileServicesOpen && (
                  <div className="pl-4 space-y-2 mt-2 border-l-2 border-gray-200">
                    {services.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        className="flex items-center py-2 text-gray-600 hover:text-black transition-colors"
                      >
                        <span className="mr-2">{service.icon}</span>
                        {service.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/tools/freight-carbon-calculator"
                className="block py-2 font-medium hover:translate-x-2 transition-transform"
              >
                Carbon Calculator
              </Link>
              <Link to="/blog" className="block py-2 font-medium hover:translate-x-2 transition-transform">
                Blog
              </Link>
              <Link to="/about" className="block py-2 font-medium hover:translate-x-2 transition-transform">
                About
              </Link>
              <Link to="/#contact" className="block py-2 font-medium hover:translate-x-2 transition-transform">
                Contact
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
